import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterRecord } from '../services/types/pagination-types';
import { parseNumberParam } from '../utils/pagination';

export interface UseUrlStateOptions {
  defaultLimit?: number;
  /**
   * Immutable base filters that define the data scope (e.g., vertical filters).
   * These are NOT stored in URL and cannot be changed by users.
   */
  defaultFilters?: FilterRecord;
  /**
   * Initial filter values applied when URL has no user-set filters.
   * These ARE stored in URL and can be changed by users.
   */
  initialFilters?: FilterRecord;
  defaultSort?: string;
  /** URL params to exclude from filter parsing (e.g., 'task', 'modal') */
  excludeParams?: string[];
}

export interface UrlState {
  limit: number;
  offset: number;
  ordering: string;
  filters: FilterRecord;
  search: string;
}

/**
 * Core hook for syncing state with URL search params.
 * Pure logic only - no UI state (input management) or side effects beyond URL updates.
 */
export const useUrlState = (options: UseUrlStateOptions = {}) => {
  const {
    defaultLimit = 50,
    defaultFilters = {},
    initialFilters = {},
    defaultSort = '',
    excludeParams = [],
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const hasSyncedInitialFilters = useRef(false);
  const hiddenBaseFilterKeys = useMemo(
    () => Object.keys(defaultFilters),
    [defaultFilters],
  );

  // Reserved params for URL state (excluding base filters which are hidden)
  const reservedKeys = useMemo(
    () => [
      'limit',
      'offset',
      'ordering',
      'search',
      ...excludeParams,
      ...hiddenBaseFilterKeys,
    ],
    [excludeParams, hiddenBaseFilterKeys],
  );

  const hasExplicitUrlState = useMemo(
    () =>
      Array.from(searchParams.keys()).some(
        (key) => !excludeParams.includes(key) && !hiddenBaseFilterKeys.includes(key),
      ),
    [excludeParams, hiddenBaseFilterKeys, searchParams],
  );

  // Derived State: Parse URL or fall back to defaults
  const state: UrlState = useMemo(() => {
    const limit = parseNumberParam(searchParams.get('limit'), defaultLimit);
    const offset = parseNumberParam(searchParams.get('offset'), 0);
    const ordering = searchParams.get('ordering') || defaultSort;
    const search = searchParams.get('search') || '';

    // 1. Start with immutable base filters
    const filters: FilterRecord = { ...defaultFilters };

    // 2. Check if we have ANY user filters in URL
    // 3. Apply initial defaults ONLY if:
    //    a) No user filters exist in URL
    //    b) We haven't already synced them (prevents reapplying after clear)
    if (
      !hasExplicitUrlState &&
      Object.keys(initialFilters).length > 0 &&
      !hasSyncedInitialFilters.current
    ) {
      Object.assign(filters, initialFilters);
    } else if (hasExplicitUrlState) {
      // 4. Parse user filters from URL
      searchParams.forEach((value, key) => {
        if (!reservedKeys.includes(key)) {
          filters[key] = value.includes(',') ? value.split(',') : value;
        }
      });
    }

    return { limit, offset, ordering, filters, search };
  }, [
    searchParams,
    defaultLimit,
    defaultSort,
    defaultFilters,
    initialFilters,
    reservedKeys,
  ]);

  // Helper to check if a sync is needed
  const shouldSyncInitialFilters = useCallback(() => {
    const hasInitials = Object.keys(initialFilters).length > 0;

    if (hasExplicitUrlState || !hasInitials || hasSyncedInitialFilters.current) {
      return false;
    }

    // Check if URL values differ from initial filters
    return Object.entries(initialFilters).some(([key, val]) => {
      const urlVal = searchParams.get(key);
      const initialVal = Array.isArray(val) ? val.join(',') : String(val);
      return urlVal !== initialVal;
    });
  }, [hasExplicitUrlState, initialFilters, searchParams]);

  // Apply initialFilters to URL if URL is "empty" (no user filters)
  useEffect(() => {
    if (shouldSyncInitialFilters()) {
      hasSyncedInitialFilters.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(initialFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              const strVal = Array.isArray(value)
                ? value.join(',')
                : String(value);
              next.set(key, strVal);
            }
          });
          return next;
        },
        { replace: true },
      );
    } else if (!hasExplicitUrlState && Object.keys(initialFilters).length > 0) {
      // Mark as synced if we skipped sync because conditions weren't met
      hasSyncedInitialFilters.current = true;
    }
  }, [
    hasExplicitUrlState,
    shouldSyncInitialFilters,
    setSearchParams,
    initialFilters,
    searchParams,
  ]);

  // Update URL helper
  const updateUrl = useCallback(
    (
      updates: Record<string, string | null | undefined>,
      replaceFilterMode = false,
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (replaceFilterMode) {
            // Remove all existing non-reserved params (clear filters)
            Array.from(next.keys()).forEach((key) => {
              if (!reservedKeys.includes(key)) {
                next.delete(key);
              }
            });
          }

          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [reservedKeys, setSearchParams],
  );

  return { state, updateUrl, searchParams, setSearchParams, defaultFilters };
};
